import React, { memo, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import { useLocation, useHistory } from 'react-router-dom';
import InfiniteScroll from 'react-infinite-scroller';
import { message } from 'antd';
import classNames from 'classnames';
import styles from './SearchPost.module.less';

import { CONTEXT, MICROCONTEXT } from '../../config';
import Loading from '../../components/Loading/Loading';
import { getQueryString } from '../../utils/utils';
import Toast from '../../components/Toast/Toast';
import Dialog from '../../components/Dialog/Dialog';
import PostLine from '../../components/Forum/ForumLine/PostLine';
import PostLineReco from '../../components/Forum/ForumLine/PostLineReco';

import { getSearchPost, getSearchPostHot } from '../../actions/searchResult';
import { logging, logPostInfo } from '../../actions/log';

const selectLogInfo = createSelector(
  state => state.log,
  log => log.logInfo,
);
const selectSearchInfo = createSelector(
  state => state.search,
  search => search,
);
const selectSearchKw = createSelector(
  state => state.layout,
  layout => layout.searchKw,
);

const selectSearchResultObj = createSelector(
  state => state.searchResult,
  searchResult => searchResult,
);

export default memo(({ scrollNode, logInfo }) => {
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();
  const logInfoData = useSelector(selectLogInfo);

  const { searchParam, searchPost, searchCount, searchPostHot } = useSelector(
    selectSearchResultObj,
  );

  const keyword = getQueryString('keyword');
  // 浏览时长打桩
  const leaveTimeLog = useCallback(
    initTime => {
      const keyword = getQueryString('keyword');
      const leaveTime = +new Date();
      dispatch(
        logging({
          category: 'I',
          action: 'View_Time',
          label: '',
          param2: {
            source: `Search_Post`,
            source_p: logInfoData.source_p,
            source_pp: logInfoData.source_pp,
            search_type: logInfoData.search_type,
            search_word: logInfoData.search_word || keyword,
            initTime,
            leaveTime,
            time: leaveTime - initTime,
            keyword,
            searchParam,
          },
          param3: searchPost.bizInfo,
        }),
      );
    },
    [logInfoData, searchParam, searchPost],
  );
  // 统计页面浏览时长
  useEffect(() => {
    const initTime = +new Date();
    return () => {
      leaveTimeLog(initTime);
    };
  }, []);

  /**
   * 当数据为activeTab数据，tab为activeTab，且请求的是第一页的数据
   * 上报请求完成的桩，当做一次搜索结果成功的上报
   */
  useEffect(() => {
    if (
      searchParam?.searchWord === searchPost?.searchParam?.searchWord &&
      searchParam?.activeTab === 'post' &&
      searchParam?.postTypeSel === searchPost?.searchParam?.postTypeSel &&
      searchPost?.loading === 200
    ) {
      // 请求数据成功
      if (searchPost?.dataList?.length > 0 && searchPost?.dataList?.length <= 48) {
        dispatch(
          logging({
            category: 'R',
            action: 'SearchResult',
            label: '',
            param2: {
              source: 'Search_Post',
              source_p: logInfoData.source_p,
              source_pp: logInfoData.source_pp,
              has_content: searchPost?.dataList?.length > 0 ? 1 : 0,
              search_type: logInfoData.search_type,
              search_word: logInfoData.search_word || keyword,
              keyword,
            },
            param3: searchPost?.bizInfo,
          }),
        );
      }
      // 请求数据为空
      if (searchPost?.dataList?.length === 0) {
        dispatch(
          logging({
            category: 'R',
            action: 'SearchResult_NoData',
            label: '',
            param2: {
              source: 'Search_Post',
              source_p: logInfoData.source_p,
              source_pp: logInfoData.source_pp,
              has_content: searchPost?.dataList?.length > 0 ? 1 : 0,
              search_type: logInfoData.search_type,
              search_word: logInfoData.search_word || keyword,
              keyword,
            },
            param3: searchPost?.bizInfo,
          }),
        );
      }
    }
  }, [searchPost, searchParam]);

  // 开始请求推荐数据
  useEffect(() => {
    dispatch(getSearchPostHot());
  }, []);

  // 当searchParam参数和  返回的数据中pageParam参数不一致的时候，就重新请求页面数据
  useEffect(() => {
    if (
      !(
        searchParam?.searchWord === searchPost?.searchParam?.searchWord &&
        searchParam?.activeTab === searchPost?.searchParam?.activeTab &&
        searchParam?.postTypeSel === searchPost?.searchParam?.postTypeSel
      )
    ) {
      let params = {
        refresh: true,
        searchWord: getQueryString('keyword'),
        postTypeSel: searchParam?.postTypeSel || '0',
        algoBizInfo: searchPost?.algoBizInfo || '',
      };
      // 如果切换搜索词
      if (searchParam?.searchWord !== searchPost?.searchParam?.searchWord) {
        params.algoBizInfo = '';
      }

      // 传入相关数据（重新）请求数据
      dispatch(getSearchPost(params));
    }
  }, [searchPost, searchParam]);

  const loadMore = useCallback(() => {
    let params = {
      searchMode: '1', // searchMode仅仅在loadmore时传入  因为useEffect并不知道是否为点击搜索按钮的搜索
      refresh: false,
      searchWord: getQueryString('keyword'),
      postTypeSel: searchParam?.postTypeSel || '0',
      algoBizInfo: searchPost?.algoBizInfo || '',
    };
    // 传入相关数据（继续）请求数据
    dispatch(getSearchPost(params));
  }, [searchPost, searchParam]);

  const handleSearchOut = () => {
    window.open(`https://www.baidu.com/s?wd=${keyword}`);
    dispatch(
      logging({
        category: 'C',
        action: 'Search_To_Browser',
        label: '',
        param2: {
          source: 'Search_Post',
          source_p: logInfoData.source_p,
          source_pp: logInfoData.source_pp,
          search_type: logInfoData.search_type,
          search_word: logInfoData.search_word || keyword,
          keyword,
        },
      }),
    );
  };

  if (
    !searchPost ||
    !searchPost?.loading ||
    searchPost?.loading !== 200 ||
    searchPost?.searchParam?.searchWord !== getQueryString('keyword')
  ) {
    return (
      <div className={styles.s_loading}>
        <Loading />
      </div>
    );
  }

  return (
    <div className={styles.s_post}>
      <InfiniteScroll
        pageStart={1}
        loadMore={loadMore}
        hasMore={searchPost?.dataList?.length < searchCount?.postsCount}
        loader={
          <div className="loader" key={0}>
            内容加载中...
          </div>
        }
        initialLoad={true}
        useWindow={false}
        getScrollParent={() => scrollNode}
        threshold={800}
      >
        {searchPost?.dataList?.length > 0 && (
          <div className={`s_list`}>
            {searchPost?.dataList?.map((item, index) => {
              return <PostLine forumData={item} key={index} logInfo={{ source: 'Search_Post' }} />;
            })}
          </div>
        )}
        {searchPost?.dataList?.length > 0 &&
          searchPost?.dataList?.length >= searchCount?.postsCount && (
            <div className={styles.search_out} onClick={handleSearchOut}>
              没找到满意结果？试试{' '}
              <span className={styles.search_out_text}>
                百度搜索 &quot;{keyword}&quot;&nbsp;&gt;
              </span>
            </div>
          )}
        {searchPost?.dataList?.length === 0 && (
          <div className={styles.Nosearchresult}>
            <div className="NosearchresultBox">
              <div className="noresultImg" />
              <div className="mainTextWrap">
                <div className="mainText" onClick={handleSearchOut}>
                  没找到匹配结果。试试&nbsp;
                  <span className={styles.search_out_text}>
                    百度搜索 &quot;{keyword}&quot;&nbsp;&gt;
                  </span>
                  <br />{' '}
                </div>
              </div>
            </div>
          </div>
        )}
        {((searchPost?.dataList?.length > 0 &&
          searchPost?.dataList?.length >= searchCount?.postsCount) ||
          !searchPost?.dataList?.length ||
          searchPost?.dataList?.length === 0) &&
          searchPostHot?.data?.length > 0 && (
            <div className={styles.s_reco}>
              <div className={'s_reco_header'}>
                <div className={'s_reco_header_title'}>社区热帖</div>
                <div
                  className={'s_reco_header_more'}
                  onClick={() => {
                    dispatch(
                      logging({
                        category: 'C',
                        action: 'Search_Post_Reco_More',
                        label: '',
                        param2: {
                          source: 'Search_Post_Reco',
                          source_p: logInfoData.source_p,
                          source_pp: logInfoData.source_pp,
                          search_type: logInfoData.search_type,
                          search_word: logInfoData.search_word || keyword,
                          keyword,
                        },
                      }),
                    );
                    dispatch(
                      logPostInfo({
                        source: 'Search_Post_Reco',
                        source_p: logInfoData.source_p,
                        source_pp: logInfoData.source_pp,
                      }),
                    );
                    history.push(`${CONTEXT}${MICROCONTEXT}${MICROCONTEXT}/forumlist`);
                  }}
                >
                  查看更多
                </div>
              </div>
              <div className={'s_reco_list'}>
                {searchPostHot?.data.slice(0, 5)?.map((item, index) => {
                  return (
                    <PostLineReco
                      forumData={item}
                      key={index}
                      indexNum={index}
                      logInfo={{ source: 'Search_Post_Reco' }}
                    />
                  );
                })}
              </div>
            </div>
          )}
      </InfiniteScroll>
    </div>
  );
});
